/**
 * Gruparea deconturilor neînchise cu termen depășit, pe angajat și pe an.
 *
 * DE CE: lista era un zid de butoane `Nume — 2026.08`, cu același nume repetat de patru-cinci
 * ori. Alexandru, 18.09.2026: „Numele se repetă, nu se justifică atâtea butoane."
 *
 * În mod normal aici nu stau multe rânduri — un decont neînchis cu termenul depășit e o excepție,
 * nu regula. De aceea secțiunea stă strânsă, iar drumul e în trei pași: o deschizi, vezi cine și
 * în ce ani, apeși pe un an și vezi lunile.
 */

export interface DecontNeinchis {
  id: number
  period: string
  employee: { id: number; fullName: string }
}

export interface AnNeinchis {
  an: string
  /** Lunile din anul ăsta, cea mai veche prima — acolo e problema cea mai mare. */
  deconturi: DecontNeinchis[]
}

export interface AngajatNeinchis {
  employeeId: number
  nume: string
  /** Câte deconturi are în total, peste toți anii. */
  total: number
  ani: AnNeinchis[]
}

/** `2026.08` → `2026`. Perioadele fără punct rămân întregi, ca să nu dispară tăcut. */
export function anulDin(period: string): string {
  const i = period.indexOf('.')
  return i > 0 ? period.slice(0, i) : period
}

/**
 * Grupează pe angajat, apoi pe an.
 *
 * Angajații se ordonează după CEL MAI VECHI decont, nu alfabetic: cine stă în urmă de un an
 * trebuie să fie primul pe listă, nu pierdut între nume care încep cu litere mai mici.
 */
export function grupeazaNeinchise(deconturi: DecontNeinchis[]): AngajatNeinchis[] {
  const peAngajat = new Map<number, DecontNeinchis[]>()
  for (const d of deconturi) {
    const lista = peAngajat.get(d.employee.id)
    if (lista) lista.push(d)
    else peAngajat.set(d.employee.id, [d])
  }

  const out: AngajatNeinchis[] = []
  for (const [employeeId, lista] of peAngajat) {
    const peAn = new Map<string, DecontNeinchis[]>()
    for (const d of lista) {
      const an = anulDin(d.period)
      const g = peAn.get(an)
      if (g) g.push(d)
      else peAn.set(an, [d])
    }
    const ani = [...peAn.entries()]
      .map(([an, ds]) => ({
        an,
        deconturi: [...ds].sort((a, b) => a.period.localeCompare(b.period)),
      }))
      .sort((a, b) => a.an.localeCompare(b.an))

    out.push({
      employeeId,
      nume: lista[0].employee.fullName,
      total: lista.length,
      ani,
    })
  }

  return out.sort((a, b) => {
    const vechimeA = a.ani[0]?.deconturi[0]?.period ?? ''
    const vechimeB = b.ani[0]?.deconturi[0]?.period ?? ''
    // Cel mai vechi primul; la egalitate, alfabetic, ca ordinea să nu sară de la o citire la alta.
    return vechimeA === vechimeB ? a.nume.localeCompare(b.nume) : vechimeA.localeCompare(vechimeB)
  })
}
